import { lazy, type ReactNode, Suspense } from "react";
import { useTranslation } from "react-i18next";
import type { Source } from "../lib/sseEvents";
import type { StreamPhase, ToolState } from "../types";
import { McpToolCard } from "./McpToolCard";
import { SourcesButton } from "./SourcesButton";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { ToolConfirmCard } from "./ToolConfirmCard";

/**
 * The markdown renderer pulls in react-markdown + highlight.js (~the largest
 * slice of the bundle) and is only needed once an assistant message has text,
 * so it's split into its own chunk. While that chunk loads (first message
 * only), the streamed raw text is shown as the fallback — no blank flash.
 */
const Markdown = lazy(() => import("./Markdown").then((m) => ({ default: m.Markdown })));

export interface AssistantMessageProps {
  content: string;
  /** Model display name for the "who" label; falls back to a generic label. */
  modelName?: string;
  /** Model provider origin, drives the brand-mark avatar. */
  modelOrigin?: string | null;
  sources?: Source[] | null;
  /** Tool calls on this turn, in order (the model can chain several). */
  tools?: ToolState[];
  /** True for the live, in-flight assistant turn. */
  streaming?: boolean;
  /** What the model is doing while no text has streamed; drives dot-vs-label. */
  streamingPhase?: StreamPhase | null;
  /** Label for the thinking indicator's specific (named) phases. */
  streamingLabel?: string;
  errored?: boolean;
  error?: string | null;
  /** Decision callback for a tool awaiting confirmation (live runs only). */
  onToolDecision?: (toolCallId: string, action: "approve" | "always" | "cancel") => void;
  /** Scopes Canvas artifact ids to this turn (server message id, or "live-assistant"). */
  messageId?: string;
  /** Per-message actions + branch switcher, composed by the caller. */
  footer?: ReactNode;
}

/** An assistant turn: sources, optional tool card, markdown body, and actions. */
export function AssistantMessage({
  content,
  sources,
  tools = [],
  streaming = false,
  streamingPhase = null,
  streamingLabel,
  errored = false,
  error,
  onToolDecision,
  messageId,
  footer,
}: AssistantMessageProps) {
  const { t } = useTranslation();
  const showThinking = streaming && !content && tools.length === 0;

  return (
    <div data-testid="assistant-message" className="group/turn py-2.5">
      {tools.map((tool) =>
        // MCP connector tools route by the proposal's connector — BEFORE the
        // legacy name heuristic, which would misread e.g. "create_image_block".
        tool.proposal?.connector ? (
          <McpToolCard
            key={tool.callId}
            tool={tool}
            onDecision={
              onToolDecision ? (action) => onToolDecision(tool.callId, action) : undefined
            }
          />
        ) : (
          <ToolConfirmCard
            key={tool.callId}
            tool={tool}
            onApprove={() => onToolDecision?.(tool.callId, "approve")}
            onCancel={() => onToolDecision?.(tool.callId, "cancel")}
          />
        ),
      )}

      {content ? (
        <div data-testid="assistant-message-content">
          <Suspense fallback={<div className="chat-md whitespace-pre-wrap">{content}</div>}>
            <Markdown content={content} sources={sources ?? undefined} messageId={messageId} />
          </Suspense>
        </div>
      ) : showThinking ? (
        <ThinkingIndicator phase={streamingPhase} label={streamingLabel ?? t("chat.thinking")} />
      ) : null}

      {errored ? (
        <p className="mt-2 font-sans text-[13px] text-danger">
          {error || t("errors.somethingWentWrong")}
        </p>
      ) : null}

      {sources && sources.length > 0 ? (
        <div className="mt-3">
          <SourcesButton sources={sources} />
        </div>
      ) : null}

      {footer}
    </div>
  );
}
